import { useMutation, useQueryClient } from '@tanstack/react-query'

import {
  demoDataApi,
  type ConvertDemoCertificateResult,
  type ConvertDemoProductResult,
} from 'services'

/**
 * The two forward-only `demo` row conversions (VRG-836).
 *
 * Two hooks rather than one with a `kind` argument, deliberately: the mutations return
 * DIFFERENT result shapes (a product reports retained certificates, a certificate reports
 * whether it metered) and a caller has to branch on which it got anyway. Folding them into
 * one would produce a union every call site then un-unions by hand.
 *
 * Both expose a per-ROW in-flight id (`mutation.variables`, which react-query holds for the
 * lifetime of the call) so a kebab can disable only the row actually converting rather than
 * every demo row on the page — §46's category 3, the same contract
 * `useDeleteIntroCertificate` documents.
 */

/** "This scraped product is accurate; keep it." Free. Does NOT reach its certificates. */
export const useConvertDemoProduct = () => {
  const queryClient = useQueryClient()

  const mutation = useMutation<ConvertDemoProductResult, Error, string>({
    mutationFn: (productId: string) => demoDataApi.convertProduct(productId),
    onSuccess: () => invalidateDemoSurfaces(queryClient),
  })

  return {
    convertDemoProductAsync: mutation.mutateAsync,
    isConvertingDemoProduct: mutation.isPending,
    convertingProductId: mutation.isPending ? mutation.variables : undefined,
    convertDemoProductError: mutation.error,
  }
}

/**
 * "This certificate is real; keep it."
 *
 * ⚠️ BILLABLE. The server clears the flag AND records the activation in one call, so the
 * certificate lands on the brand's next invoice. The modal that fires this must say so
 * BEFORE it is called — see `ConvertDemoRowModal`.
 *
 * `billing` is in the invalidation set for that reason and not for symmetry: an activation
 * was just recorded, so any usage/invoice surface the user navigates to next is stale.
 */
export const useConvertDemoCertificate = () => {
  const queryClient = useQueryClient()

  const mutation = useMutation<ConvertDemoCertificateResult, Error, string>({
    mutationFn: (certificateId: string) => demoDataApi.convertCertificate(certificateId),
    onSuccess: () => invalidateDemoSurfaces(queryClient),
  })

  return {
    convertDemoCertificateAsync: mutation.mutateAsync,
    isConvertingDemoCertificate: mutation.isPending,
    convertingCertificateId: mutation.isPending ? mutation.variables : undefined,
    convertDemoCertificateError: mutation.error,
  }
}

/**
 * A conversion changes what the badge-bearing lists AND the billing surfaces return, so the
 * invalidation is broad for the same reason `invalidateIntroSurfaces` is: this fires a
 * handful of times in a tenant's life, and the cost of a refetch is irrelevant next to the
 * cost of a list still showing a "Demo" badge on a row the user just converted.
 *
 * Prefix-matched keys, so every filtered/paginated variant is caught.
 */
function invalidateDemoSurfaces(queryClient: ReturnType<typeof useQueryClient>) {
  // Verified prefixes, not guesses: list reads are `['products'|'certificates', 'list', ...]`
  // and the detail reads are `[..., 'detail', id]`, so one prefix each covers both.
  // `['billing']` covers `upcoming-invoice` and `activation-usage` (`useSubscription.ts`),
  // which are exactly the two a certificate conversion has just changed.
  for (const key of [['products'], ['certificates'], ['analytics'], ['billing']]) {
    queryClient.invalidateQueries({ queryKey: key })
  }
}
